// src/hooks/useAuthSync.js
import { useEffect, useRef } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { getClientId } from "../utils/clientId";
import { getApiBase } from "../utils/apiBase";
import {
  claimPendingReferral,
  clearPendingReferralCode,
  getPendingReferralCode,
} from "../utils/inviteFriends";

const API_BASE = getApiBase({ trailingSlash: true });

export function useAuthSync() {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const inFlightRef = useRef(false);
  const lastSyncedSignatureRef = useRef("");
  const profileSignature = [
    user?.username || "",
    user?.firstName || "",
    user?.lastName || "",
    user?.imageUrl || "",
    user?.primaryEmailAddress?.emailAddress || "",
    user?.primaryPhoneNumber?.phoneNumber || "",
  ].join("|");

  useEffect(() => {
    if (!isSignedIn) return;

    const clientId = getClientId();
    if (!clientId) return;

    const syncSignature = `${clientId}|${profileSignature}`;
    if (inFlightRef.current || lastSyncedSignatureRef.current === syncSignature) return;

    inFlightRef.current = true;

    (async () => {
      try {
        const token = await getToken();

        const url = `${API_BASE}auth/sync-counter`;
        console.log("[useAuthSync] calling", url, "with clientId", clientId);

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-client-id": clientId,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ clientId }),
          credentials: "include",
        });

        console.log("[useAuthSync] status", res.status);

        if (!res.ok) {
          console.error("sync-counter failed", res.status);
          return;
        }

        const pendingReferralCode = getPendingReferralCode();
        if (pendingReferralCode) {
          try {
            const referralResult = await claimPendingReferral(
              getToken,
              clientId,
              pendingReferralCode
            );
            if (referralResult?.processed) {
              clearPendingReferralCode();
            }
          } catch (referralError) {
            console.error("claim-pending-referral error", referralError);
          }
        }

        lastSyncedSignatureRef.current = syncSignature;
        window.dispatchEvent(new Event("qubiq-notifications-refresh"));
      } catch (err) {
        console.error("sync-counter error", err);
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [isSignedIn, getToken, profileSignature]);
}
